import { useState } from 'react';
import clsx from 'clsx';

import { Card, Button, Input, Badge, Icon } from '@lib/ui';
import type { WizardPersona } from '../types';
import {
  generateMockContent,
  SAMPLE_PROMPTS,
  CHANNELS,
  DECISION_DIMENSIONS,
  defaultDecisions,
} from '../types';

export type PersonaTestPanelProps = {
  persona: Partial<WizardPersona>;
  onFeedback?: (feedback: 'yes' | 'no' | 'refine') => void;
  className?: string;
};

interface GeneratedSample {
  channel: string;
  topic: string;
  content: string;
  highlights: string[];
}

function PersonaTestPanel({ persona, onFeedback, className }: PersonaTestPanelProps) {
  const [customTopic, setCustomTopic] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('LinkedIn');
  const [samples, setSamples] = useState<GeneratedSample[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const availableChannels = CHANNELS.slice(0, 4);
  const decisions = persona.decisions || defaultDecisions;

  // Convert decision keys to human-readable labels
  const getHighlightLabel = (highlight: string): string => {
    const dimension = DECISION_DIMENSIONS.find((d) => d.key === highlight);
    if (dimension) {
      const level = dimension.levels.find((l) => l.value === decisions[dimension.key]);
      return level?.label || highlight;
    }
    return highlight;
  };

  const handleGenerate = (topic: string) => {
    setIsGenerating(true);

    // Simulate generation delay
    setTimeout(() => {
      const { content, highlights } = generateMockContent({
        persona,
        channel: selectedChannel,
        topic,
      });

      setSamples((prev) => [
        {
          channel: selectedChannel,
          topic,
          content,
          highlights: highlights.map(getHighlightLabel),
        },
        ...prev,
      ]);
      setIsGenerating(false);
    }, 500);
  };

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Test Your Persona</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">
          See how your persona would write about different topics
        </p>
      </div>

      {/* Channel selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
          Select Channel
        </label>
        <div className="flex flex-wrap gap-2">
          {availableChannels.map((channel) => (
            <Badge
              key={channel}
              variant={channel === selectedChannel ? 'filled' : 'outlined'}
              color={channel === selectedChannel ? 'primary' : 'neutral'}
              className="cursor-pointer transition-colors"
              onClick={() => setSelectedChannel(channel)}
            >
              {channel}
            </Badge>
          ))}
        </div>
      </div>

      {/* Topic input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
          Enter a Topic
        </label>
        <div className="flex gap-2">
          <Input
            value={customTopic}
            onChange={(e) => setCustomTopic(e.target.value)}
            placeholder="e.g., The future of AI in content creation"
            className="flex-1"
          />
          <Button
            onClick={() => customTopic && handleGenerate(customTopic)}
            disabled={!customTopic || isGenerating}
          >
            <Icon name="sparkles" size="md" />
            <span className="ml-1">Generate</span>
          </Button>
        </div>
      </div>

      {/* Quick prompts */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
          Or try a sample prompt
        </label>
        <div className="flex flex-wrap gap-2">
          {SAMPLE_PROMPTS.map((prompt) => (
            <Button
              key={prompt}
              variant="soft"
              className="text-xs px-2 py-1"
              onClick={() => handleGenerate(prompt)}
              disabled={isGenerating}
            >
              {prompt}
            </Button>
          ))}
        </div>
      </div>

      {/* Generated samples */}
      {samples.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 dark:text-white">Generated Samples</h4>

          {samples.map((sample, idx) => (
            <Card key={idx} skin="bordered" className="overflow-hidden">
              {/* Sample header */}
              <div className="px-4 py-2 bg-gray-50 dark:bg-dark-700 border-b border-gray-200 dark:border-dark-600 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="soft" color="info">
                    {sample.channel}
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-dark-300">"{sample.topic}"</span>
                </div>
              </div>

              {/* Sample content */}
              <div className="p-4">
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 dark:text-dark-200">
                  {sample.content}
                </pre>

                {/* Decision highlights */}
                {sample.highlights.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {sample.highlights.map((h, i) => (
                      <Badge key={i} variant="soft" color="success" className="text-xs">
                        {h}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Feedback buttons */}
              <div className="px-4 py-3 bg-gray-50 dark:bg-dark-700 border-t border-gray-200 dark:border-dark-600">
                <p className="text-sm text-gray-600 dark:text-dark-300 mb-2">
                  Does this sound like your persona?
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="soft"
                    color="success"
                    className="text-xs px-2 py-1"
                    onClick={() => onFeedback?.('yes')}
                  >
                    <Icon name="check" size="md" />
                    <span className="ml-1">Yes</span>
                  </Button>
                  <Button
                    variant="soft"
                    color="error"
                    className="text-xs px-2 py-1"
                    onClick={() => onFeedback?.('no')}
                  >
                    <Icon name="close" size="md" />
                    <span className="ml-1">No</span>
                  </Button>
                  <Button
                    variant="soft"
                    color="warning"
                    className="text-xs px-2 py-1"
                    onClick={() => onFeedback?.('refine')}
                  >
                    <Icon name="edit" size="md" />
                    <span className="ml-1">Refine</span>
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {samples.length === 0 && (
        <Card skin="bordered" className="p-8 text-center">
          <div className="text-gray-400 dark:text-dark-500 mb-3 flex justify-center">
            <Icon name="sparkles" size="md" />
          </div>
          <p className="text-sm text-gray-500 dark:text-dark-400">
            Enter a topic or select a sample prompt to see how your persona writes
          </p>
        </Card>
      )}
    </div>
  );
}

PersonaTestPanel.displayName = 'PersonaTestPanel';

export { PersonaTestPanel };
